import { useState, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Package, Plus } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate } from 'react-router-dom';
import { useOrdensServico } from '@/hooks/useOrdensServico';
import { OrdemServicoFormDialog } from '@/components/ordens/OrdemServicoFormDialog';

interface ProfileOrdersProps {
  clienteId: string;
  /** Cliente pré-selecionado para a OS (pré-preenche nome/telefone/endereço). */
  clientePreSelecionado?: ComponentProps<typeof OrdemServicoFormDialog>['clientePreSelecionado'];
}

export function ProfileOrders({ clienteId, clientePreSelecionado }: ProfileOrdersProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: ordens, isLoading } = useOrdensServico();
  const [dialogCriarOS, setDialogCriarOS] = useState(false);

  // Filtrar ordens do cliente e pegar as 4 mais recentes
  const ordensCliente = ordens?.filter((o) => o.cliente_id === clienteId)?.slice(0, 4) || [];

  if (isLoading) {
    return (
      <div className="painel__profile-section">
        <div className="painel__profile-section-hdr">
          <span className="painel__profile-section-title">{t('panel.lastServices')}</span>
        </div>
        {[1, 2].map((i) => (
          <Skeleton key={i} className="h-14 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  // First active/in-progress order gets the "ink invert" thumb treatment
  const activeId = ordensCliente.find(
    (o) => o.status === 'aceita' || o.status === 'em_execucao',
  )?.id;

  return (
    <div className="painel__profile-section">
      <div className="painel__profile-section-hdr">
        <span className="painel__profile-section-title">{t('panel.lastServices')}</span>
        {ordensCliente.length > 0 && (
          <button
            type="button"
            className="painel__profile-section-link"
            onClick={() => navigate('/ordens')}
          >
            {t('common.seeAll', 'Ver todas')}
          </button>
        )}
      </div>
      <div className="painel__profile-section-actions">
        <button
          type="button"
          className="painel__profile-section-link painel__profile-section-link--action"
          onClick={() => setDialogCriarOS(true)}
        >
          <Plus size={13} />
          {t('clients.actions.createOS', 'Criar OS')}
        </button>
      </div>

      {ordensCliente.length === 0 ? (
        <div className="text-center py-4 text-muted-foreground">
          <Package className="h-7 w-7 mx-auto mb-2 opacity-50" />
          <p className="text-xs">{t('panel.noServicesRegistered')}</p>
        </div>
      ) : (
        ordensCliente.map((ordem) => {
          const isActive = ordem.id === activeId;
          const titulo = ordem.servicos?.[0]?.descricao || ordem.servicos?.[0]?.tipo || `OS ${ordem.numero_os ?? ''}`;
          const sub = [ordem.endereco?.cidade, ordem.endereco?.rua].filter(Boolean).join(' · ');
          return (
            <button
              key={ordem.id}
              type="button"
              onClick={() => navigate(`/ordens/${ordem.id}`)}
              className={`painel__svc-row${isActive ? ' painel__svc-row--active' : ''}`}
            >
              <div
                className={`painel__svc-thumb${isActive ? ' painel__svc-thumb--ink' : ''}`}
                aria-hidden
              >
                <Package size={18} />
              </div>
              <div className="min-w-0">
                <div className="painel__svc-title">{titulo}</div>
                {sub && <div className="painel__svc-sub">{sub}</div>}
              </div>
              <span className="painel__svc-code">#{ordem.numero_os || '----'}</span>
            </button>
          );
        })
      )}

      <OrdemServicoFormDialog
        open={dialogCriarOS}
        onOpenChange={setDialogCriarOS}
        clientePreSelecionado={clientePreSelecionado ?? clienteId}
      />
    </div>
  );
}
